import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useApiClient from "./useApiClient";
import useDebouncedQueryParams from "./useDebouncedQueryParams";
import { dropdownHandlerForQueryParamsFactory } from "../components/dropdowns/utils";
import { dataAssetSearchDefaults } from "../schemas/dataAssetSearchSchema";
import {
  mapDataAssetSearchParamsToApiParams,
  mapDataAssetStatisticsParamsToApiParams,
} from "./dataAssetSearchParams";
import useDataAssetPreviewNavigator from "./useDataAssetPreviewNavigator";
import { cancelRequestOnSignal } from "@/apiUtils/cancelableQuery";
import { isSearchQueryTooShort } from "@/utils/searchQueryValidation";

type DataAssetSearchDefaults = typeof dataAssetSearchDefaults;

type DateRangeValue = {
  startDate: string | null;
  endDate: string | null;
};

const defaultFilterParams = dataAssetSearchDefaults;

const defaultFilterChipParams = {
  dataSetPublicIds: defaultFilterParams.dataSetPublicIds,
  manifestPublicIds: defaultFilterParams.manifestPublicIds,
  realAssetPublicIds: defaultFilterParams.realAssetPublicIds,
  realAssetDeploymentPublicIds:
    defaultFilterParams.realAssetDeploymentPublicIds,
  sealogEventValues: defaultFilterParams.sealogEventValues,
  sealogEventFreeTexts: defaultFilterParams.sealogEventFreeTexts,
  sealogEventOptionNames: defaultFilterParams.sealogEventOptionNames,
  sealogEventOptionValues: defaultFilterParams.sealogEventOptionValues,
  fileTypes: defaultFilterParams.fileTypes,
};

const useDataAssetList = (
  shouldGrabFocus = true,
  excludeFromClear: string[] = [],
  excludedFilters: string[] = []
) => {
  const { rawValues, setRawValues, setDebouncedQueryParams, queryParams } =
    useDebouncedQueryParams<DataAssetSearchDefaults>(
      ["query", "sealogEventQuery"],
      defaultFilterParams
    );

  const api = useApiClient();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (shouldGrabFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [shouldGrabFocus]);

  const [searchTypePreference, setSearchTypePreference] = useState<
    "Files" | "Sealog Events"
  >("Files");

  const specifiedSearchType = rawValues.sealogEventQuery
    ? "Sealog Events"
    : rawValues.query
    ? "Files"
    : searchTypePreference;

  const handleSpecifiedSearchTypeChange = (
    newType: "Files" | "Sealog Events"
  ) => {
    setSearchTypePreference(newType);
    if (newType === "Files" && rawValues.sealogEventQuery) {
      setRawValues({
        query: rawValues.sealogEventQuery,
        sealogEventQuery: "",
      });
      setDebouncedQueryParams({
        query: rawValues.sealogEventQuery,
        sealogEventQuery: "",
        page: 1,
      });
    }
    if (newType === "Sealog Events" && rawValues.query) {
      setRawValues({
        sealogEventQuery: rawValues.query,
        query: "",
      });
      setDebouncedQueryParams({
        sealogEventQuery: rawValues.query,
        query: "",
        page: 1,
      });
    }
  };

  const makeDropdownChangeHandler = dropdownHandlerForQueryParamsFactory({
    rawValues,
    setRawValues,
  });

  const handleRealAssetSelection =
    makeDropdownChangeHandler("realAssetPublicIds");
  const handleDeploymentSelection = makeDropdownChangeHandler(
    "realAssetDeploymentPublicIds"
  );
  const handleManifestSelection =
    makeDropdownChangeHandler("manifestPublicIds");
  const handleDataSetSelection = makeDropdownChangeHandler("dataSetPublicIds");
  const handleSealogEventValueSelection =
    makeDropdownChangeHandler("sealogEventValues");
  const handleSealogEventFreeTextSelection = makeDropdownChangeHandler(
    "sealogEventFreeTexts"
  );
  const handleSealogEventOptionNameSelection = makeDropdownChangeHandler(
    "sealogEventOptionNames"
  );
  const handleSealogEventOptionValueSelection = makeDropdownChangeHandler(
    "sealogEventOptionValues"
  );
  const handleFileTypesSelection = makeDropdownChangeHandler("fileTypes");

  const handleSortChange = (newSortOrder: string) => {
    setRawValues({ sortOrder: newSortOrder });
    setDebouncedQueryParams({ sortOrder: newSortOrder });
  };

  const handleDateValueChange = (newValue: DateRangeValue) => {
    const startDate = newValue.startDate
      ? `${newValue.startDate}T00:00:00Z`
      : null;
    const endDate = newValue.endDate ? `${newValue.endDate}T00:00:00Z` : null;

    setRawValues({
      assetModifiedAfter: startDate,
      assetModifiedBefore: endDate,
    });

    setDebouncedQueryParams({
      assetModifiedAfter: startDate,
      assetModifiedBefore: endDate,
    });
  };

  const handleRegexToggleChange = (newCheckedState: boolean) => {
    setRawValues({ regex: newCheckedState });
    setDebouncedQueryParams({ regex: newCheckedState });
  };

  const hasNonDefaultValuesSet = (
    defaults: Record<string, unknown>,
    params: Record<string, unknown>
  ): boolean =>
    Object.keys(params).some((key) => {
      return (
        key in defaults &&
        JSON.stringify(params[key]) !== JSON.stringify(defaults[key])
      );
    });

  const hasFilterChipActive = useMemo(() => {
    const filterToKeyMap = {
      fileType: "fileTypes",
      realAsset: "realAssetPublicIds",
      deployment: "realAssetDeploymentPublicIds",
      eventTypes: "sealogEventValues",
      eventDescriptions: "sealogEventFreeTexts",
      eventAdditionalTypes: "sealogEventOptionNames",
      eventAdditionalDescriptions: "sealogEventOptionValues",
      dataSet: "dataSetPublicIds",
      manifest: "manifestPublicIds",
    };

    const keysToCheck = Object.entries(filterToKeyMap)
      .filter(([filterName, key]) => {
        return (
          !excludeFromClear.includes(key) &&
          !excludedFilters.includes(filterName)
        );
      })
      .map(([, key]) => key);

    return keysToCheck.some((key) => {
      const isNonDefault =
        JSON.stringify(rawValues[key as keyof DataAssetSearchDefaults]) !==
        JSON.stringify(
          defaultFilterChipParams[key as keyof typeof defaultFilterChipParams]
        );
      return isNonDefault;
    });
  }, [rawValues, excludeFromClear, excludedFilters]);

  const handleClearAll = () => {
    const preservedValues = Object.fromEntries(
      excludeFromClear.map((key) => [
        key,
        rawValues[key as keyof DataAssetSearchDefaults],
      ])
    ) as Partial<DataAssetSearchDefaults>;

    setRawValues({ ...defaultFilterChipParams, ...preservedValues });
    setDebouncedQueryParams({ ...defaultFilterChipParams, ...preservedValues });
  };

  const hasSearchParams = hasNonDefaultValuesSet(
    defaultFilterParams,
    queryParams
  );

  // The API rejects non-regex terms under the search index's 3-character
  // floor (422): show immediate feedback from the raw input and never fire
  // the debounced request.
  const searchQueryTooShort = isSearchQueryTooShort(
    rawValues.query,
    rawValues.regex
  );
  const searchIsRunnable =
    hasSearchParams &&
    !searchQueryTooShort &&
    !isSearchQueryTooShort(
      queryParams.query as string,
      queryParams.regex as boolean
    );

  const { isPending, data, isPlaceholderData } = useQuery({
    queryKey: ["data-asset-list", queryParams],
    queryFn: ({ signal }) =>
      cancelRequestOnSignal(
        api.dataAccess.searchDataAssets(
          mapDataAssetSearchParamsToApiParams(
            queryParams as unknown as Record<string, unknown>
          )
        ),
        signal
      ),
    throwOnError: true,
    refetchOnWindowFocus: false,
    enabled: searchIsRunnable,
    placeholderData: keepPreviousData,
  });

  const statsQueryParams = mapDataAssetStatisticsParamsToApiParams(queryParams);

  const { isPending: searchStatsAreLoading, data: searchStatsData } = useQuery({
    queryKey: ["data-asset-search-stats", statsQueryParams],
    queryFn: ({ signal }) =>
      cancelRequestOnSignal(
        api.dataAccess.getSearchStatistics(statsQueryParams),
        signal
      ),
    throwOnError: true,
    refetchOnWindowFocus: false,
    enabled: searchIsRunnable,
    staleTime: 60_000,
  });

  const previewNavigator = useDataAssetPreviewNavigator({
    data,
    queryParams: queryParams as unknown as Record<string, unknown>,
    totalItems: searchStatsData?.number_of_assets,
  });

  return {
    rawValues,
    setRawValues,
    queryParams,
    inputRef,
    handleRealAssetSelection,
    handleDeploymentSelection,
    handleManifestSelection,
    handleDataSetSelection,
    handleSealogEventValueSelection,
    handleSealogEventFreeTextSelection,
    handleSealogEventOptionNameSelection,
    handleSealogEventOptionValueSelection,
    handleFileTypesSelection,
    handleSortChange,
    handleClearAll,
    handleRegexToggleChange,
    handleDateValueChange,
    specifiedSearchType,
    handleSpecifiedSearchTypeChange,
    hasFilterChipActive,
    hasSearchParams,
    searchIsRunnable,
    searchQueryTooShort,
    isLoading: searchIsRunnable && isPending,
    data,
    isPreviousData: isPlaceholderData,
    searchStatsAreLoading: searchIsRunnable && searchStatsAreLoading,
    searchStatsData,
    setDebouncedQueryParams,
    previewNavigator,
  };
};

export default useDataAssetList;
